export const CIRENIO_SDK_LOAD_TIMEOUT_MS = 8000;

export const CIRENIO_WIDGET_LOAD_ERROR =
  'No se pudo cargar el widget de pagos. Revisa tu conexión o vuelve a intentarlo.';

function isAbortedError(error) {
  return error?.name === 'AbortError' || error?.message === 'aborted';
}

function rejectIfAborted(signal) {
  if (signal?.aborted) {
    const error = new Error('aborted');
    error.name = 'AbortError';
    throw error;
  }
}

/**
 * Loads the Cirenio PSP script and resolves only when window.CirenioPayments exists.
 * Empty onload, network block, missing src and timeouts all reject (except abort).
 */
export function loadCirenioPaymentsSdk(
  scriptSrc,
  { timeoutMs = CIRENIO_SDK_LOAD_TIMEOUT_MS, signal } = {}
) {
  return new Promise((resolve, reject) => {
    if (!scriptSrc) {
      reject(new Error('missing_src'));
      return;
    }

    let settled = false;
    let timeoutId;
    let script;
    let onSdkReady;
    let onScriptError;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      if (script) {
        script.removeEventListener('load', onSdkReady);
        script.removeEventListener('error', onScriptError);
      }
      fn(value);
    };

    const onAbort = () => {
      finish(reject, Object.assign(new Error('aborted'), { name: 'AbortError' }));
    };

    try {
      rejectIfAborted(signal);
    } catch (error) {
      reject(error);
      return;
    }

    if (typeof window !== 'undefined' && window.CirenioPayments) {
      resolve(window.CirenioPayments);
      return;
    }

    signal?.addEventListener('abort', onAbort);

    onSdkReady = () => {
      if (window.CirenioPayments) {
        finish(resolve, window.CirenioPayments);
        return;
      }
      finish(reject, new Error('sdk_missing'));
    };

    onScriptError = () => finish(reject, new Error('script_error'));

    script = document.querySelector(`script[src="${scriptSrc}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      document.body.appendChild(script);
    }

    script.addEventListener('load', onSdkReady);
    script.addEventListener('error', onScriptError);

    timeoutId = setTimeout(() => {
      if (window.CirenioPayments) {
        finish(resolve, window.CirenioPayments);
        return;
      }
      finish(reject, new Error('timeout'));
    }, timeoutMs);
  });
}

export function isCirenioLoadAborted(error) {
  return isAbortedError(error);
}
